import React, { Suspense, lazy } from "react";
import { Routes, BrowserRouter, Route } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import { useSelector } from "react-redux";
import { profileSideBar } from "./components/elements/language";
import ProtectedRoute from "./protectedRoute";
import FreeAuthRoute from "./FreeAuthRoute";
import CheckProfile from "./middlewares/CheckProfile";
import IncomingCallHost from "./components/socket/IncomingCallHost";
import CallErrorBoundary from "./components/elements/CallErrorBoundary";

// Lazy-loaded Layouts
const AuthLayout = lazy(() => import("./layouts/AuthLayout"));
const DefaultLayout = lazy(() => import("./layouts/DefaultLayout"));
const InnerLayout = lazy(() => import("./layouts/InnerLayout"));
const BlankLayout = lazy(() => import("./layouts/BlankLayout"));

// Lazy-loaded Pages and Components
const Signin = lazy(() => import("./pages/Signin"));
const Home = lazy(() => import("./pages/Home"));
const FindPsychologist = lazy(() => import("./pages/FindPsychologist"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const AreaIntervention = lazy(() => import("./pages/AreaIntervention"));
const CommunityPsychology = lazy(() => import("./pages/CommunityPsychology"));
const Contact = lazy(() => import("./pages/Contact"));
const SignUp = lazy(() => import("./pages/SignUp"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Faq = lazy(() => import("./pages/Faq"));
const ControlPanel = lazy(() => import("./pages/profile/ControlPanel"));
const ConsultantDetails = lazy(() => import("./pages/ConsultantDetails"));
const ForgotPassword = lazy(() => import("./components/sections/signup/ForgotPassword"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ConsultantSignup = lazy(() => import("./pages/profile/ConsultantSignup"));
const InterventionDetails = lazy(() => import("./pages/InterventionDetails"));
const EmailChats = lazy(() => import("./pages/EmailChats"));
const Chats = lazy(() => import("./pages/Chats"));
const MyProfile = lazy(() => import("./pages/profile/MyProfile"));
const Appointments = lazy(() => import("./pages/profile/Appointments"));
const MyClients = lazy(() => import("./pages/profile/MyClients"));
const Reviews = lazy(() => import("./pages/profile/Reviews"));
const AppointmentsDetails = lazy(() => import("./pages/profile/AppuntamentiPassatiDetails"));
const UserEmailDetails = lazy(() => import("./components/items/emailchatBox/userEmailDetails"));
const ClientNoteList = lazy(() => import("./components/items/clientNotes/ClientNoteList"));
const WishList = lazy(() => import("./pages/profile/WishList"));
const TextChat = lazy(() => import("./pages/TextChat"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const TermsandConditions = lazy(() => import("./pages/TermsandConditions"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));
const NoInternet = lazy(() => import("./pages/NoInternet"));
const BlogDetails = lazy(() => import("./components/sections/blogs/BlogDetails"));
const AdminInvoice = lazy(() => import("./components/elements/tabContent/profile/appuntamentidetails/adminInvoice"));

const CreateRoutes = () => {
  const localLang = useSelector((state) => state.languageSlice.data);
  const language = localStorage.getItem("language") || localLang;
  const user = useSelector((state) => state?.initialProfileSlice?.data);
  const id = user && user?._id;

  return (
    <BrowserRouter>
      {/* Global ring host — must outlive layout/page changes so Call Now works on
          home, profile, appointments, find-psychologist, mobile & desktop. */}
      <IncomingCallHost />
      <ScrollToTop>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/signin" element={<AuthLayout />}>
              <Route
                path="/signin"
                exact
                element={
                  <FreeAuthRoute>
                    <Signin />
                  </FreeAuthRoute>
                }
              />
            </Route>
            <Route path="/signup" element={<AuthLayout />}>
              <Route
                path="/signup"
                exact
                element={
                  <FreeAuthRoute>
                    <SignUp />
                  </FreeAuthRoute>
                }
              />
            </Route>
            <Route path="/signup-consultant" element={<AuthLayout />}>
              <Route
                path="/signup-consultant"
                exact
                element={
                  <FreeAuthRoute>
                    <ConsultantSignup />
                  </FreeAuthRoute>
                }
              />
            </Route>
            <Route path="/forgot-password" element={<AuthLayout />}>
              <Route
                path="/forgot-password"
                exact
                element={
                  <FreeAuthRoute>
                    <ForgotPassword />
                  </FreeAuthRoute>
                }
              />
            </Route>
            <Route path="/feedback" element={<AuthLayout />}>
              <Route path="/feedback" exact element={<Feedback />} />
            </Route>
            <Route path="/consultant-profile/:id" element={<AuthLayout />}>
              <Route path="/consultant-profile/:id" exact element={<ConsultantDetails />} />
            </Route>
            <Route path="/" element={<DefaultLayout />}>
              <Route path="/" exact element={<Home />} />
            </Route>
            <Route path="/find-psychologist" element={<DefaultLayout />}>
              <Route
                path="/find-psychologist"
                exact
                element={
                  <ProtectedRoute>
                    <FindPsychologist />{" "}
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="/howitworks" element={<DefaultLayout />}>
              <Route path="/howitworks" exact element={<HowItWorks />} />
            </Route>
            <Route path="/about" element={<DefaultLayout />}>
              <Route path="/about" exact element={<About />} />
            </Route>
            <Route path="/blog" element={<DefaultLayout />}>
              <Route path="/blog" exact element={<Blog />} />
            </Route>
            <Route path="/blog-details/:id" element={<DefaultLayout />}>
              <Route path="/blog-details/:id" exact element={<BlogDetails />} />
            </Route>
            <Route path="/terms-of-services" element={<DefaultLayout />}>
              <Route path="/terms-of-services" exact element={<TermsandConditions />} />
            </Route>
            <Route path="/privacy-policy" element={<DefaultLayout />}>
              <Route path="/privacy-policy" exact element={<PrivacyPolicyPage />} />
            </Route>
            <Route path="/area-of-intervention" element={<DefaultLayout />}>
              <Route path="/area-of-intervention" exact element={<AreaIntervention />} />
            </Route>
            <Route path="/community-psychology" element={<DefaultLayout />}>
              <Route path="/community-psychology" exact element={<CommunityPsychology />} />
            </Route>
            <Route path="/contact-us" element={<DefaultLayout />}>
              <Route path="/contact-us" exact element={<Contact />} />
            </Route>
            <Route path="/frequently-asked-questions" element={<DefaultLayout />}>
              <Route path="/frequently-asked-questions" exact element={<Faq />} />
            </Route>
            <Route
              path="/control-panel"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.controlPanel?.[language]} />
                </ProtectedRoute>
              }>
              <Route
                path="/control-panel"
                exact
                element={
                  <CheckProfile>
                    <ControlPanel />
                  </CheckProfile>
                }
              />
            </Route>
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.myProfile?.[language]} />
                </ProtectedRoute>
              }>
              <Route path="/profile" exact element={<MyProfile />} />
            </Route>
            <Route
              path="/view-online"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.viewOnline?.[language]} />
                </ProtectedRoute>
              }>
              <Route path="/view-online" exact element={<ConsultantDetails userId={id} />} />
            </Route>
            <Route
              path="/wish-list"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.wishList?.[language]} />
                </ProtectedRoute>
              }>
              <Route
                path="/wish-list"
                exact
                element={
                  <CheckProfile>
                    <WishList />
                  </CheckProfile>
                }
              />
            </Route>
            <Route
              path="/appointments"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.appointments?.[language]} />
                </ProtectedRoute>
              }>
              <Route
                path="/appointments"
                exact
                element={
                  <CheckProfile>
                    <Appointments />
                  </CheckProfile>
                }
              />
            </Route>
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.myClients?.[language]} />
                </ProtectedRoute>
              }>
              <Route
                path="/clients"
                exact
                element={
                  <CheckProfile>
                    <MyClients />
                  </CheckProfile>
                }
              />
            </Route>
            <Route
              path="/client-notes/:id"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.myClients?.[language]} />
                </ProtectedRoute>
              }>
              <Route path="/client-notes/:id" exact element={<ClientNoteList />} />
            </Route>
            <Route
              path="/email"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle="E-mail" />
                </ProtectedRoute>
              }>
              <Route
                path="/email"
                exact
                element={
                  <CheckProfile>
                    <EmailChats />
                  </CheckProfile>
                }
              />
            </Route>
            <Route
              path="/email-details/:id"
              element={
                <ProtectedRoute>
                  <InnerLayout />
                </ProtectedRoute>
              }>
              <Route path="/email-details/:id" exact element={<UserEmailDetails />} />
            </Route>
            <Route
              path="/reviews"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.myReviews?.[language]} />
                </ProtectedRoute>
              }>
              <Route
                path="/reviews"
                exact
                element={
                  <CheckProfile>
                    <Reviews />
                  </CheckProfile>
                }
              />
            </Route>
            <Route
              path="/appointments-details"
              element={
                <ProtectedRoute>
                  <InnerLayout profileTitle={profileSideBar?.appointments?.[language]} />
                </ProtectedRoute>
              }>
              <Route path="/appointments-details/:id" exact element={<AppointmentsDetails />} />
            </Route>
            <Route path="/intervention-details/:id" element={<DefaultLayout />}>
              <Route path="/intervention-details/:id" exact element={<InterventionDetails />} />
            </Route>

            <Route
              path="/text-chat/:id"
              element={
                <ProtectedRoute>
                  <BlankLayout />
                </ProtectedRoute>
              }>
              <Route
                path="/text-chat/:id"
                exact
                element={
                  <CallErrorBoundary>
                    <TextChat />
                  </CallErrorBoundary>
                }
              />
            </Route>

            <Route
              path="/chats/:id"
              element={
                <ProtectedRoute>
                  <BlankLayout />
                </ProtectedRoute>
              }>
              <Route
                path="/chats/:id"
                exact
                element={
                  <CallErrorBoundary>
                    <Chats />
                  </CallErrorBoundary>
                }
              />
            </Route>

            {/* <Route
              path="/waiting/:id"
              element={
                <ProtectedRoute>
                  <BlankLayout />
                </ProtectedRoute>
              }
            >
            <Route path="/waiting/:id" exact element={<WaitingPage />} />
            </Route> */}

            <Route path="/review-appointment/:id">
              <Route
                path="/review-appointment/:id"
                exact
                element={
                  <ProtectedRoute>
                    <ReviewPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="invoice" element={<AdminInvoice />} />

            <Route path="*" element={<NotFoundPage />} />
            <Route
              path="/no-connection"
              element={
                <DefaultLayout>
                  <NoInternet />
                </DefaultLayout>
              }
              exact
            />
          </Routes>
        </Suspense>
      </ScrollToTop>
    </BrowserRouter>
  );
};

export default CreateRoutes;
